import React from 'react';
import { BookOpen, PenLine, ShieldCheck, Sparkles } from 'lucide-react';
import chiefEditorImg from '../assets/chife_editor.png';

const highlights = [
  {
    title: 'Medical Journalism',
    description: 'Deep expertise in translating complex science into clear, credible reporting.',
    icon: PenLine,
  },
  {
    title: 'Scientific Integrity',
    description: 'Ensures every piece maintains accuracy, balance, and clinical relevance.',
    icon: ShieldCheck,
  },
  {
    title: 'Continuous Education',
    description: 'Champions accessible, practice-oriented learning for healthcare professionals.',
    icon: BookOpen,
  },
  {
    title: 'Digital Innovation',
    description: 'Advances scientific storytelling through modern publishing and educational initiatives.',
    icon: Sparkles,
  },
];

const ChiefEditor = () => {
  return (
    <main className="font-sans text-slate-600">
      {/* Hero / Profile */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" aria-labelledby="chief-editor-heading">
        <div className="mx-auto max-w-[1180px] px-6 sm:px-10 lg:px-12">
          <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,380px)_1fr] lg:gap-16">
            {/* Portrait */}
            <div className="mx-auto w-full max-w-sm lg:mx-0">
              <div className="relative overflow-hidden rounded-2xl border border-slate-100 bg-slate-50 shadow-[0_20px_60px_rgba(11,31,58,0.16)]">
                <img
                  src={chiefEditorImg}
                  alt="Ms Meena Srivastav, Chief Editor of HB MedIntel"
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

            {/* Intro */}
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-brand-700">
                Chief Editor
              </p>
              <span className="mt-2 block h-[3px] w-12 bg-teal-accent" aria-hidden />

              <h1
                id="chief-editor-heading"
                className="mt-5 font-playfair text-[2rem] font-bold leading-[1.2] text-navy-900 sm:text-[2.25rem] lg:text-[2.5rem]"
              >
                Ms Meena Srivastav
              </h1>
              <p className="mt-2 text-base font-medium text-brand-700">
                Chief Editor, HB MedIntel
              </p>

              <p className="mt-6 max-w-2xl text-[15px] leading-[1.8] text-slate-600">
                Ms Meena Srivastav is the Chief Editor of HB MedIntel, a dedicated educational platform
                focused on delivering credible, simplified, and clinically relevant healthcare updates for
                medical professionals across specialties. With a strong background in healthcare
                communication, medical publishing, and scientific content strategy, she has played a pivotal
                role in bridging the gap between complex medical science and practical clinical understanding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="bg-slate-50 py-16 sm:py-20 lg:py-24" aria-labelledby="chief-editor-bio-heading">
        <div className="mx-auto max-w-4xl px-6 sm:px-10 lg:px-12">
          <div className="text-center">
            <span className="mx-auto block h-[3px] w-14 bg-teal-accent" aria-hidden />
            <h2
              id="chief-editor-bio-heading"
              className="mt-5 font-playfair text-3xl font-bold text-navy-900 sm:text-[2.1rem]"
            >
              A Voice for Trusted Medical Knowledge
            </h2>
          </div>

          <div className="mt-10 space-y-6 text-[15px] leading-[1.85] text-slate-600">
            <p>
              Over the years, she has collaborated extensively with leading physicians, medical associations,
              researchers, and healthcare organizations to curate high-quality educational resources,
              conference highlights, expert interviews, clinical updates, and evidence-based scientific
              content for healthcare professionals.
            </p>
            <p>
              Under her editorial leadership, HB MedIntel has emerged as a trusted knowledge platform
              committed to continuous medical education and dissemination of the latest advancements in
              medicine, therapeutics, and patient care practices.
            </p>
            <p>
              Although not a practicing doctor, she brings deep expertise in medical journalism, healthcare
              education, scientific storytelling, and strategic medical communication, ensuring that all
              published content maintains scientific integrity, accuracy, and relevance for the medical
              community.
            </p>
            <p>
              Meena is particularly passionate about making medical knowledge more accessible, engaging, and
              practice-oriented through digital innovation, scientific publishing, and educational initiatives
              aimed at empowering healthcare professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Areas of Expertise */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" aria-labelledby="chief-editor-expertise-heading">
        <div className="mx-auto max-w-[1180px] px-6 sm:px-10 lg:px-12">
          <div className="mx-auto max-w-2xl text-center">
            <span className="mx-auto block h-[3px] w-14 bg-teal-accent" aria-hidden />
            <h2
              id="chief-editor-expertise-heading"
              className="mt-5 font-playfair text-3xl font-bold text-navy-900 sm:text-[2.1rem]"
            >
              Areas of Expertise
            </h2>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {highlights.map(({ title, description, icon: Icon }) => (
              <li
                key={title}
                className="rounded-xl border border-slate-100 bg-white p-6 shadow-card transition-shadow hover:shadow-card-hover"
              >
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50">
                  <Icon className="h-[22px] w-[22px] text-brand-700" strokeWidth={1.75} aria-hidden />
                </div>
                <h3 className="text-[15px] font-semibold leading-snug text-navy-900">{title}</h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-slate-500">{description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
};

export default ChiefEditor;
